import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, PackageCheck, CheckCircle2, AlertTriangle, Clock, Wallet, ChevronLeft, Phone } from "lucide-react";
import { fetchEscrow, verifyEscrow, escrowAction } from "@/lib/api";
import { fmtPrice, withWatermark } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";

export const ESCROW_STATUS = {
  pending_payment: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  paid: { label: "Fonds securises — en attente de livraison", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  shipped: { label: "Livraison declaree — confirmez la reception", cls: "bg-sky-50 text-sky-800 border-sky-300" },
  disputed: { label: "Litige en cours — fonds geles", cls: "bg-red-50 text-red-800 border-red-300" },
  released: { label: "Reception confirmee — versement vendeur en cours", cls: "bg-violet-50 text-violet-800 border-violet-300" },
  paid_out: { label: "Terminee — vendeur paye", cls: "bg-gray-100 text-gray-700 border-gray-300" },
  refunded: { label: "Rembourse a l'acheteur", cls: "bg-rose-50 text-rose-800 border-rose-300" },
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

export default function EscrowStatusPage() {
  const { ref } = useParams();
  const [params, setParams] = useSearchParams();
  const { user, loading: authLoading, openAuthModal } = useAuth();
  const [esc, setEsc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("non_recu");
  const [details, setDetails] = useState("");
  const [disputeOpen, setDisputeOpen] = useState(false);

  const load = () => fetchEscrow(ref).then(setEsc).catch((e) => setErr(e?.response?.data?.detail || "Transaction introuvable"));

  useEffect(() => {
    if (authLoading) return;
    if (!user) { openAuthModal(); return; }
    if (params.get("paiement")) {
      setBusy(true);
      verifyEscrow(ref).then((r) => { setEsc(r.escrow); if (r.payment_status === "paid") toast.success("Paiement securise confirme !"); else if (r.payment_status === "pending") toast.info("Paiement en cours de confirmation…"); })
        .catch(() => load())
        .finally(() => { setBusy(false); params.delete("paiement"); params.delete("tx"); params.delete("status"); params.delete("dryrun"); setParams(params, { replace: true }); });
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, user, authLoading]);

  const act = async (action, body) => {
    setBusy(true); setErr("");
    try { setEsc(await escrowAction(ref, action, body)); toast.success("Action enregistree"); setDisputeOpen(false); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (authLoading || (!esc && !err)) return <main className="max-w-3xl mx-auto px-6 py-24 text-center"><Loader2 className="animate-spin mx-auto text-emerald-600" /></main>;
  if (err && !esc) return <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="escrow-error"><p className="font-bold">{err}</p><Link to="/annonces" className="btn-primary mt-4 inline-block">Retour</Link></main>;

  const st = ESCROW_STATUS[esc.status] || { label: esc.status, cls: "" };
  const isBuyer = esc.role === "buyer";
  const isSeller = esc.role === "seller";

  return (
    <main className="max-w-3xl mx-auto px-6 py-10" data-testid="escrow-page">
      <Link to="/mes-transactions" className="text-sm inline-flex items-center gap-1 text-[var(--ink-soft)]"><ChevronLeft size={14} /> Mes transactions securisees</Link>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2 inline-flex items-center gap-2"><ShieldCheck className="text-emerald-600" /> Paiement securise <span className="font-mono text-lg text-[var(--ink-soft)]">{esc.ref}</span></h1>
      <div className={`mt-4 rounded-2xl border-2 px-4 py-3 font-bold ${st.cls}`} data-testid="escrow-status">{st.label}</div>

      <section className="glass-card p-5 mt-5 flex gap-4 items-start">
        {esc.annonce_image && <img src={withWatermark(esc.annonce_image)} alt="" className="w-24 h-24 rounded-xl object-cover" />}
        <div className="flex-1 text-sm">
          <Link to={`/annonces/${esc.annonce_ref || esc.annonce_id}`} className="font-bold hover:underline" data-testid="escrow-annonce-link">{esc.annonce_title}</Link>
          <div className="mt-1 grid grid-cols-2 gap-1">
            <span className="text-[var(--ink-soft)]">Montant securise</span><b data-testid="escrow-amount">{fmtPrice(esc.amount)}</b>
            <span className="text-[var(--ink-soft)]">Frais protection (5 %)</span><span>{fmtPrice(esc.commission)}</span>
            <span className="text-[var(--ink-soft)]">Versement vendeur</span><b>{fmtPrice(esc.seller_amount)}</b>
            <span className="text-[var(--ink-soft)]">{isBuyer ? "Vendeur" : "Acheteur"}</span>
            <span>{isBuyer ? esc.seller_name : esc.buyer_name}{(isBuyer ? esc.seller_phone : esc.buyer_phone) && esc.status !== "pending_payment" && <a href={`tel:${isBuyer ? esc.seller_phone : esc.buyer_phone}`} className="ml-2 inline-flex items-center gap-1 text-emerald-700"><Phone size={12} /> appeler</a>}</span>
          </div>
          {esc.auto_release_at && esc.status === "shipped" && <div className="mt-2 text-xs inline-flex items-center gap-1 text-sky-800"><Clock size={12} /> Liberation automatique le {fmtDate(esc.auto_release_at)} sans action de l&apos;acheteur</div>}
        </div>
      </section>

      {/* Actions */}
      <section className="mt-5 space-y-3" data-testid="escrow-actions">
        {isSeller && esc.status === "paid" && (
          <button onClick={() => act("ship")} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="escrow-ship-btn"><PackageCheck size={16} /> J&apos;ai livre l&apos;article</button>
        )}
        {isBuyer && (esc.status === "paid" || esc.status === "shipped") && (
          <>
            <button onClick={() => act("confirm")} disabled={busy} className="btn-green w-full inline-flex items-center justify-center gap-2" data-testid="escrow-confirm-btn"><CheckCircle2 size={16} /> J&apos;ai bien recu l&apos;article — payer le vendeur</button>
            <button onClick={() => setDisputeOpen((v) => !v)} disabled={busy} className="w-full text-sm font-semibold text-red-700 underline inline-flex items-center justify-center gap-1" data-testid="escrow-dispute-toggle"><AlertTriangle size={14} /> Signaler un probleme</button>
            {disputeOpen && (
              <div className="glass-card p-4 space-y-2" data-testid="escrow-dispute-form">
                <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="escrow-dispute-reason">
                  {Object.entries(esc.dispute_reasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Decrivez le probleme (optionnel)" data-testid="escrow-dispute-details" />
                <button onClick={() => act("dispute", { reason, details })} disabled={busy} className="btn-primary !bg-red-600 w-full" data-testid="escrow-dispute-submit">Ouvrir le litige (fonds geles)</button>
              </div>
            )}
          </>
        )}
        {esc.status === "released" && isSeller && <div className="rounded-2xl bg-violet-50 border border-violet-200 p-4 text-sm inline-flex items-center gap-2 w-full"><Wallet size={16} className="text-violet-700" /> Votre versement de <b>{fmtPrice(esc.seller_amount)}</b> est en cours de traitement par Fodindin (Mobile Money).</div>}
        {esc.status === "pending_payment" && isBuyer && (
          <button onClick={() => { setBusy(true); verifyEscrow(ref).then((r) => setEsc(r.escrow)).finally(() => setBusy(false)); }} disabled={busy} className="btn-secondary w-full" data-testid="escrow-verify-btn">Verifier mon paiement</button>
        )}
        {err && <div className="text-sm font-semibold text-red-600" data-testid="escrow-action-error">{err}</div>}
      </section>

      <section className="glass-card p-5 mt-6" data-testid="escrow-timeline">
        <h2 className="font-bold text-sm mb-3">Historique</h2>
        <ol className="space-y-2 text-sm">
          {(esc.timeline || []).slice().reverse().map((t, i) => (
            <li key={i} className="flex gap-3"><span className="text-xs text-[var(--ink-soft)] w-32 shrink-0">{fmtDate(t.at)}</span><span>{t.note}</span></li>
          ))}
        </ol>
      </section>
    </main>
  );
}
